// Pure estimate engine for coaching assignments (LG-313). No I/O — imported
// by both the tray UI and the server-side planning service so the two can
// never disagree. All tuning knobs live in ESTIMATE_TUNING; a future
// calibration pass over startedAt/completedAt actuals replaces these priors.

// Single source of the pace vocabulary — the router's zod enum, the slice
// contract, and the tray's <option> list all derive from this tuple. The pg
// enum in db/schema/enums.ts is the one necessary hand-copy (schema files
// can't import app modules); keep the two in sync.
export const COACHING_PACES = ['fast', 'average', 'slow'] as const

export type CoachingPace = (typeof COACHING_PACES)[number]

export interface DerivedEstimate {
  low: number
  mid: number
  high: number
}

export const ESTIMATE_TUNING = {
  floorMinutes: 10,
  ceilingMinutes: 90,
  baseMinMinutes: 15,
  baseMaxMinutes: 65,
  eloScale: 400,
  paceMultipliers: { fast: 0.8, average: 1, slow: 1.3 },
  redoMultiplier: 0.6,
  spreadFloor: 0.05,
  spreadSlope: 0.15,
  stepMinutes: 5,
  // Last-resort estimate when a card has no override, no ELO derivation, and
  // no catalog value — it counts as this everywhere (tray display, day
  // budgets, auto-placement) without the coach touching anything.
  defaultEstimateMinutes: 30,
} as const

const clampMinutes = (minutes: number): number =>
  Math.min(
    ESTIMATE_TUNING.ceilingMinutes,
    Math.max(ESTIMATE_TUNING.floorMinutes, minutes),
  )

const roundToStep = (minutes: number): number =>
  Math.round(minutes / ESTIMATE_TUNING.stepMinutes) *
  ESTIMATE_TUNING.stepMinutes

export function derivedEstimate(input: {
  clientElo: number | null | undefined
  problemElo: number | null | undefined
  pace: CoachingPace
  isRedo: boolean
}): DerivedEstimate | null {
  // Number.isFinite(null | undefined | NaN | Infinity) is always false, so
  // this one check covers missing ELO as well as the Postgres double
  // precision edge cases (NaN/±Infinity) that would otherwise flow through
  // the curve, produce a NaN estimate, and silently defeat budget checks.
  const clientElo = input.clientElo ?? Number.NaN
  const problemElo = input.problemElo ?? Number.NaN
  if (!Number.isFinite(clientElo) || !Number.isFinite(problemElo)) return null
  const delta = problemElo - clientElo
  const difficulty = 1 / (1 + 10 ** (-delta / ESTIMATE_TUNING.eloScale))
  const base =
    ESTIMATE_TUNING.baseMinMinutes +
    (ESTIMATE_TUNING.baseMaxMinutes - ESTIMATE_TUNING.baseMinMinutes) *
      difficulty
  // Widened lookup: pace reaches here from DB row mappers that assert (not
  // validate) their shapes, so an unexpected value must fall back to the
  // average multiplier instead of producing NaN estimates.
  const paceMultipliers: Record<string, number> =
    ESTIMATE_TUNING.paceMultipliers
  const scaled =
    base *
    (paceMultipliers[input.pace] ?? ESTIMATE_TUNING.paceMultipliers.average) *
    (input.isRedo ? ESTIMATE_TUNING.redoMultiplier : 1)
  const spread =
    ESTIMATE_TUNING.spreadFloor + ESTIMATE_TUNING.spreadSlope * difficulty
  return {
    low: roundToStep(clampMinutes(scaled * (1 - spread))),
    mid: roundToStep(clampMinutes(scaled)),
    high: roundToStep(clampMinutes(scaled * (1 + spread))),
  }
}

export function effectiveEstimateMinutes(input: {
  overrideMinutes: number | null | undefined
  clientElo: number | null | undefined
  problemElo: number | null | undefined
  pace: CoachingPace
  isRedo: boolean
  catalogMinutes: number | null | undefined
}): number {
  if (input.overrideMinutes != null) return input.overrideMinutes
  const derived = derivedEstimate(input)
  if (derived) return derived.mid
  return input.catalogMinutes ?? ESTIMATE_TUNING.defaultEstimateMinutes
}
